import { useTranslation } from '../../../shared/i18n';
import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, Form, Input, Select, Button, Row, Col, Typography, Spin, message } from 'antd';
import { SendOutlined, CheckCircleFilled } from '@ant-design/icons';
import PageHeader from '../../../shared/components/PageHeader';
import supportService from '../../services/supportService';
import { userPath } from '../../../shared/config/paths';
import { uploadFiles, patchFile } from '../../../shared/utils/directUpload';
import { AttachmentPicker, PRIORITY_META, DEFAULT_LIMITS, ok } from './common';

const { Text } = Typography;

/** 部门单选卡片（value = department id） */
const DepartmentCards = ({ value, onChange, departments }) => {
    const { t } = useTranslation();
    return (
        <Row gutter={[12, 12]}>
            {departments.map((d) => {
                const active = value === d.id;
                return (
                    <Col key={d.id} xs={24} sm={12} md={8}>
                        <Card
                            hoverable
                            size="small"
                            role="radio"
                            aria-checked={active}
                            tabIndex={0}
                            onClick={() => onChange && onChange(d.id)}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange && onChange(d.id); } }}
                            style={{
                                height: '100%',
                                borderColor: active ? 'var(--ant-color-primary, #1677ff)' : undefined,
                                boxShadow: active ? '0 0 0 1px var(--ant-color-primary, #1677ff)' : undefined,
                            }}
                        >
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                <Text strong>{t(d.name)}</Text>
                                {active && <CheckCircleFilled style={{ color: 'var(--ant-color-primary, #1677ff)' }} />}
                            </div>
                            {d.description && <Text type="secondary" style={{ fontSize: 12 }}>{t(d.description)}</Text>}
                        </Card>
                    </Col>
                );
            })}
        </Row>
    );
};

const NewTicket = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [options, setOptions] = useState({ departments: [], services: [], priorities: Object.keys(PRIORITY_META), limits: DEFAULT_LIMITS });

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const r = await supportService.getOptions();
                if (!alive) return;
                if (!ok(r)) { message.error(r?.msg || t('Failed to load')); return; }
                const d = r.data || {};
                const departments = Array.isArray(d.departments) ? d.departments : [];
                const services = Array.isArray(d.services) ? d.services : [];
                setOptions({
                    departments,
                    services,
                    priorities: Array.isArray(d.priorities) && d.priorities.length ? d.priorities : Object.keys(PRIORITY_META),
                    limits: { ...DEFAULT_LIMITS, ...(d.limits || {}) },
                });
                // 深链预选：?department=billing  ?type=evm&id=123
                // ?topic=account-deletion：官网 /account-deletion 的入口(技术支持 + 预填主题和内容)
                const topic = searchParams.get('topic') === 'account-deletion';
                const depKey = searchParams.get('department') || (topic ? 'support' : null);
                const dep = departments.find((x) => x.key === depKey) || departments[0];
                if (topic) {
                    form.setFieldsValue({
                        subject: t('Account deletion request'),
                        body: t('Please delete my Yeap Cloud account and all of its data.'),
                    });
                }
                const type = searchParams.get('type');
                const id = searchParams.get('id');
                const svc = type && id ? services.find((s) => s.type === type && String(s.id) === String(id)) : null;
                form.setFieldsValue({
                    department_id: dep ? dep.id : undefined,
                    service_type: svc ? svc.type : '',
                    service: svc ? `${svc.type}:${svc.id}` : undefined,
                });
            } catch (e) {
                if (alive) message.error(e?.msg || t('Failed to load'));
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // two steps: product type first (only the types the user has), then the service of that type
    const serviceType = Form.useWatch('service_type', form);
    const typeOptions = useMemo(() => {
        const types = {};
        options.services.forEach((s) => {
            if (!types[s.type]) types[s.type] = { label: s.type_label || s.type, n: 0 };
            types[s.type].n += 1;
        });
        return [
            { value: '', label: t('None') },
            ...Object.entries(types).map(([type, x]) => ({ value: type, label: `${t(x.label)} (${x.n})` })),
        ];
    }, [options.services, t]);
    const serviceOptions = useMemo(() => options.services
        .filter((s) => s.type === serviceType)
        .map((s) => ({ value: `${s.type}:${s.id}`, label: s.label })), [options.services, serviceType]);

    const handleSubmit = async (values) => {
        const [svcType, resourceId] = values.service_type && values.service ? values.service.split(':') : [null, null];
        setSubmitting(true);
        try {
            const attachments = await uploadFiles(values.attachments || [], supportService.getUploadUrl, (uid, patch) => {
                form.setFieldsValue({ attachments: patchFile(form.getFieldValue('attachments'), uid, patch) });
            });
            const r = await supportService.createTicket({
                department_id: values.department_id,
                subject: values.subject.trim(),
                body: values.body.trim(),
                priority: values.priority,
                service_type: svcType,
                resource_id: resourceId,
            }, attachments);
            if (ok(r)) {
                message.success(t('Ticket submitted'));
                const id = r.data?.ticket?.id;
                navigate(userPath(id ? `/support/tickets/${id}` : '/support/tickets'), { replace: true });
            } else {
                message.error(r?.msg || t('Submission failed'));
            }
        } catch (e) {
            message.error(e?.msg || t('Submission failed'));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div>
            <PageHeader title={t('New ticket')} onBack={() => navigate(userPath('/support/tickets'))} />
            <Card bordered={false} style={{ maxWidth: 880 }}>
                <Spin spinning={loading}>
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        initialValues={{ priority: 'normal', service_type: '', attachments: [] }}
                        onValuesChange={(changed) => { if ('service_type' in changed) form.setFieldsValue({ service: undefined }); }}
                        disabled={submitting}
                    >
                        <Form.Item name="department_id" label={t('Department')} rules={[{ required: true, message: t('Please select a department') }]}>
                            <DepartmentCards departments={options.departments} />
                        </Form.Item>
                        <Row gutter={16}>
                            <Col xs={24} md={7}>
                                <Form.Item name="service_type" label={t('Related service')}>
                                    <Select options={typeOptions} />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={11}>
                                <Form.Item
                                    name="service"
                                    label={t('Service')}
                                    rules={serviceType ? [{ required: true, message: t('Choose a service') }] : []}
                                >
                                    <Select
                                        showSearch
                                        disabled={!serviceType}
                                        options={serviceOptions}
                                        optionFilterProp="label"
                                        placeholder={serviceType ? t('Search by name') : t('Choose a type first')}
                                    />
                                </Form.Item>
                            </Col>
                            <Col xs={24} md={6}>
                                <Form.Item name="priority" label={t('Priority')}>
                                    <Select options={options.priorities.map((p) => ({ value: p, label: t(PRIORITY_META[p]?.label || p) }))} />
                                </Form.Item>
                            </Col>
                        </Row>
                        <Form.Item
                            name="subject"
                            label={t('Subject')}
                            rules={[{ required: true, whitespace: true, min: 2, message: t('Please enter a subject') }]}
                        >
                            <Input maxLength={200} placeholder={t('Describe the issue in one sentence')} />
                        </Form.Item>
                        <Form.Item
                            name="body"
                            label={t('Description')}
                            rules={[{ required: true, whitespace: true, min: 5, message: t('Please describe your issue') }]}
                        >
                            <Input.TextArea autoSize={{ minRows: 6, maxRows: 16 }} maxLength={20000} />
                        </Form.Item>
                        <Form.Item name="attachments" valuePropName="value">
                            <AttachmentPicker limits={options.limits} />
                        </Form.Item>
                        <Form.Item style={{ marginBottom: 0 }}>
                            <Button type="primary" htmlType="submit" icon={<SendOutlined />} loading={submitting} disabled={loading}>
                                {t('New ticket')}
                            </Button>
                        </Form.Item>
                    </Form>
                </Spin>
            </Card>
        </div>
    );
};

export default NewTicket;
